import { AnyAction } from 'redux';
import * as actionTypes from '../../../../models/actionTypes';
import { IAttachmentsState, initialAttachmentsState } from '../../../../models/reduxState';
import { deepCopyProperty } from '../../../../utilities';
import { ErrorCodes } from '../../models/requestModels';

const handleSupportDataGrabSuccess = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  return {
    ...state,
    isAttachmentsEnabledConsumer: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsAttachmentsEnabledConsumer,
    isAttachmentsEnabledCommercial: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsAttachmentsEnabledCommercial,
    isAttachmentsEnabledBA: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsAttachmentsEnabledBA,
    attachmentsConfig: !!action.supportSettingsConfig ? action.supportSettingsConfig.AttachmentsConfig : null,
  };
};

const handleSocDataGrabSuccess = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  return {
    ...state,
    isAttachmentsEnabledConsumer: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsAttachmentsEnabledConsumer,
    isAttachmentsEnabledCommercial: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsAttachmentsEnabledCommercial,
    isAttachmentsEnabledBA: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsAttachmentsEnabledBA,
    attachmentsConfig: !!action.supportSettingsConfig ? action.supportSettingsConfig.AttachmentsConfig : null,
  };
};

const handleAddAttachment = (
  state: IAttachmentsState = initialAttachmentsState
): IAttachmentsState => {
  return {
    ...state,
    isUploadAttachmentInProgress: true,
  };
};

const handleGetUploadAttachmentToken = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  return {
    ...state,
    isUploadAttachmentInProgress: true,
  };
};

const handleGetUploadAttachmentTokenSuccess = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  return {
    ...state,
    isUploadAttachmentInProgress: true,
  };
};

const handleGetUploadAttachmentTokenError = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  return {
    ...state,
    isUploadAttachmentInProgress: false,
    attachmentError: ErrorCodes.AttachmentUploadFileError,
  };
};

const handleUploadFileMetadataSuccess = (
  state: IAttachmentsState = initialAttachmentsState
): IAttachmentsState => {
  return {
    ...state,
    isUploadAttachmentInProgress: true,
  };
};

const handleUploadFileMetadataError = (
  state: IAttachmentsState = initialAttachmentsState
): IAttachmentsState => {
  return {
    ...state,
    isUploadAttachmentInProgress: false,
    attachmentError: ErrorCodes.AttachmentUploadFileError,
  };
};

const handleUploadFileChunkError = (
  state: IAttachmentsState = initialAttachmentsState
): IAttachmentsState => {
  // Handle user cancel upload case, should not trigger error
  if (!!state.isAttachmentActionAborted) {
    return {
      ...state,
      isUploadAttachmentInProgress: false,
    };
  } else {
    return {
      ...state,
      isUploadAttachmentInProgress: false,
      attachmentError: ErrorCodes.AttachmentUploadFileError,
    };
  }
};

const handleCancelUploadAttachment = (
  state: IAttachmentsState = initialAttachmentsState
): IAttachmentsState => {
  return {
    ...state,
    isUploadAttachmentInProgress: false,
  };
};

const handleSaveAttachmentApiCancelTokenId = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  if (!!action.cancelTokenId) {
    const attachmentsState = { ...state };
    const cancelTokenIdList = !!attachmentsState.cancelTokenIdsList ? deepCopyProperty(attachmentsState.cancelTokenIdsList): [];
    cancelTokenIdList.push(action.cancelTokenId);
    return {
      ...state,
      cancelTokenIdsList: cancelTokenIdList,
    };
  } else {
    return state;
  }
};

const handleFlushAttachmentApiCancelTokenIds = (
  state: IAttachmentsState = initialAttachmentsState
): IAttachmentsState => {
  return {
    ...state,
    cancelTokenIdsList: [],
  };
};

const handleAttachmentActionCompletedAction = (
  state: IAttachmentsState = initialAttachmentsState
): IAttachmentsState => {
  // Handle user cancel upload case, should not trigger error
  return {
    ...state,
    isUploadAttachmentInProgress: false,
  };
};

const handleAttachmentActionAbortedAction = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  return {
    ...state,
    isAttachmentActionAborted: action.isAborted,
  };
};

const handleUploadAttachmentSuccess = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  return {
    ...state,
    isUploadAttachmentInProgress: false,
  };
};

const handleSetAttachmentError = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  return {
    ...state,
    isUploadAttachmentInProgress: false,
    attachmentError: action.error,
  };
};

/**
 * Whenever you define a new property to IAttachmentsState, consider the need to reset it if it's impacted by search or ticket creation
 */
const handleSearchSessionReset = (state: IAttachmentsState = initialAttachmentsState) => {
  return {
      ...state,
      isUploadAttachmentInProgress: initialAttachmentsState.isUploadAttachmentInProgress,
      attachmentError: initialAttachmentsState.attachmentError,
  };
};

export const AttachmentsReducer = (
  state: IAttachmentsState = initialAttachmentsState,
  action: AnyAction
): IAttachmentsState => {
  switch (action.type) {
    case actionTypes.GET_EMERALD_SUPPORTCONFIG_SUCCESS:
      return handleSupportDataGrabSuccess(state, action);
    case actionTypes.GET_EMERALD_CONFIG_SUCCESS:
      return handleSocDataGrabSuccess(state, action);
    case actionTypes.ADD_ATTACHMENT:
      return handleAddAttachment(state);
    case actionTypes.GET_ATTACHMENT_UPLOAD_TOKEN:
      return handleGetUploadAttachmentToken(state, action);
    case actionTypes.GET_ATTACHMENT_UPLOAD_TOKEN_SUCCESS:
      return handleGetUploadAttachmentTokenSuccess(state, action);
    case actionTypes.GET_ATTACHMENT_UPLOAD_TOKEN_ERROR:
      return handleGetUploadAttachmentTokenError(state, action);
    case actionTypes.UPLOAD_FILE_METADATA_SUCCESS:
      return handleUploadFileMetadataSuccess(state);
    case actionTypes.UPLOAD_FILE_METADATA_ERROR:
      return handleUploadFileMetadataError(state);
    case actionTypes.UPLOAD_FILE_CHUNK_ERROR:
      return handleUploadFileChunkError(state);
    case actionTypes.CANCEL_UPLOAD_ATTACHMENT:
      return handleCancelUploadAttachment(state);
    case actionTypes.SAVE_ATTACHMENT_API_CANCEL_TOKEN_ID:
      return handleSaveAttachmentApiCancelTokenId(state, action);
    case actionTypes.FLUSH_ATTACHMENT_API_CANCEL_TOKEN_IDS:
      return handleFlushAttachmentApiCancelTokenIds(state);
    case actionTypes.ATTACHMENT_ACTION_COMPLETED_ACTION:
      return handleAttachmentActionCompletedAction(state);
    case actionTypes.ATTACHMENT_ACTION_ABORTED_ACTION:
      return handleAttachmentActionAbortedAction(state, action);
    case actionTypes.UPLOAD_ATTACHMENT_SUCCESS:
      return handleUploadAttachmentSuccess(state, action);
    case actionTypes.SET_ATTACHMENT_ERROR:
      return handleSetAttachmentError(state, action);
    case actionTypes.RESET_SEARCH_SESSION:
      return handleSearchSessionReset(state);
    default:
      return state;
    }
  };
